import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuChartColumn, LuChevronDown, LuChevronsDownUp, LuChevronsUpDown, LuCircleCheck, LuFileSpreadsheet, LuLoaderCircle, LuMapPin, LuTriangleAlert, LuUserSearch, LuUserX, LuUsers, LuUsersRound } from 'react-icons/lu';
import { collection, getDocs, doc, updateDoc, deleteField } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import { EmptyState, HelpNote, SearchField } from '../ProfilePage/components/ProfileUi';
import useCenterText from './centerText';
import { Pill, RichText, StatusBadge } from './parts/centerBits';

/**
 * SMO Genel Bakış (Tailwind): centers grouped by their SMO with worker counts
 * from the projects, the employees of each center, centers without an SMO,
 * Excel (Turkish) and removing an SMO from a center. The logic is the old one.
 */
const getInitials = (name = '', surname = '') => {
  const a = (name || '').trim().charAt(0).toUpperCase();
  const b = (surname || '').trim().charAt(0).toUpperCase();
  return `${a}${b}` || '?';
};

const SMOOverviewModal = ({ isOpen, onClose, centers, employeesMap, onCenterUpdated }) => {
  const { t, language, label, richText } = useCenterText();
  // Toasts read the current language through a ref (the loaders run on open only)
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [workerCounts, setWorkerCounts] = useState({}); // centerId -> count
  const [centerEmployees, setCenterEmployees] = useState({}); // centerId -> [emp...]
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [expandedCenters, setExpandedCenters] = useState({}); // centerId -> bool
  const [confirmRemove, setConfirmRemove] = useState(null); // { centerId, centerName, smoName }
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const fetchWorkerCounts = async () => {
      try {
        setLoadingWorkers(true);
        const snap = await getDocs(collection(db, 'Projects'));
        const counts = {};
        const byCenter = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const employees = data.employees || [];
          const projectTitle = data.titleShort || data.titleLong || '';
          employees.forEach((emp) => {
            if (!emp.centerId) return;
            counts[emp.centerId] = (counts[emp.centerId] || 0) + 1;
            if (!byCenter[emp.centerId]) byCenter[emp.centerId] = [];
            byCenter[emp.centerId].push({
              id: emp.id,
              name: emp.name || '',
              surname: emp.surname || '',
              role: emp.role || '',
              workerType: emp.workerType || '',
              blindStatus: emp.blindStatus || '',
              status: emp.status || 'active',
              projectId: d.id,
              projectTitle,
            });
          });
        });
        // Sort employees alphabetically per center
        Object.keys(byCenter).forEach((cid) => {
          byCenter[cid].sort((a, b) =>
            `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr')
          );
        });
        setWorkerCounts(counts);
        setCenterEmployees(byCenter);
      } catch (err) {
        console.error('Error fetching worker counts:', err);
      } finally {
        setLoadingWorkers(false);
      }
    };
    fetchWorkerCounts();
  }, [isOpen]);

  const toggleCenter = (centerId) => {
    setExpandedCenters((prev) => ({ ...prev, [centerId]: !prev[centerId] }));
  };

  // Group centers by SMO
  const { smoGroups, centersWithoutSmo } = useMemo(() => {
    const groups = {};
    const orphans = [];
    (centers || []).forEach((c) => {
      const smoId = c.SMO;
      if (!smoId) {
        orphans.push(c);
        return;
      }
      if (!groups[smoId]) {
        const fullName = employeesMap[smoId] || smoId;
        const parts = fullName.split(' ');
        groups[smoId] = {
          id: smoId,
          fullName,
          name: parts[0] || '',
          surname: parts.slice(1).join(' ') || '',
          centers: [],
        };
      }
      groups[smoId].centers.push(c);
    });

    const list = Object.values(groups).sort((a, b) =>
      a.fullName.localeCompare(b.fullName, 'tr')
    );
    return { smoGroups: list, centersWithoutSmo: orphans };
  }, [centers, employeesMap]);

  const filteredSmoGroups = useMemo(() => {
    if (!search.trim()) return smoGroups;
    const q = search.toLowerCase();
    return smoGroups.filter((g) => {
      if (g.fullName.toLowerCase().includes(q)) return true;
      return g.centers.some(
        (c) =>
          (c['Adı'] && c['Adı'].toLowerCase().includes(q)) ||
          (c['İl'] && c['İl'].toLowerCase().includes(q)) ||
          (c['İlçe'] && c['İlçe'].toLowerCase().includes(q))
      );
    });
  }, [smoGroups, search]);

  const stats = useMemo(() => {
    const totalSmoCenters = smoGroups.reduce((acc, g) => acc + g.centers.length, 0);
    const activeSmoCenters = smoGroups.reduce(
      (acc, g) => acc + g.centers.filter((c) => c['Durum'] === 'Aktif').length,
      0
    );
    const avg =
      smoGroups.length > 0
        ? (totalSmoCenters / smoGroups.length).toFixed(1)
        : '0';
    return {
      smoCount: smoGroups.length,
      totalSmoCenters,
      activeSmoCenters,
      orphanCount: centersWithoutSmo.length,
      avg,
    };
  }, [smoGroups, centersWithoutSmo]);

  const toggleExpand = (smoId) => {
    setExpanded((prev) => ({ ...prev, [smoId]: !prev[smoId] }));
  };

  const expandAll = () => {
    const all = {};
    filteredSmoGroups.forEach((g) => {
      all[g.id] = true;
    });
    setExpanded(all);
  };

  const collapseAll = () => setExpanded({});

  const exportExcel = () => {
    try {
      const rows = [];
      smoGroups.forEach((g) => {
        g.centers.forEach((c) => {
          rows.push({
            'SMO Adı': g.fullName,
            'Merkez Adı': c['Adı'] || '',
            'İl': c['İl'] || '',
            'İlçe': c['İlçe'] || '',
            'Durum': c['Durum'] || '',
            'Çalışan Sayısı': workerCounts[c.id] || 0,
            'Adres': c['Adres'] || '',
          });
        });
      });
      centersWithoutSmo.forEach((c) => {
        rows.push({
          'SMO Adı': '— Atanmamış —',
          'Merkez Adı': c['Adı'] || '',
          'İl': c['İl'] || '',
          'İlçe': c['İlçe'] || '',
          'Durum': c['Durum'] || '',
          'Çalışan Sayısı': workerCounts[c.id] || 0,
          'Adres': c['Adres'] || '',
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SMO Genel Bakış');
      XLSX.writeFile(wb, 'SMO_GenelBakis.xlsx');
      toast.success(msg('smoExcelDone'));
    } catch (err) {
      console.error(err);
      toast.error(msg('smoExcelError'));
    }
  };

  // Remove SMO assignment from a single center.
  // 1) Clears `SMO` field on the Center doc.
  // 2) Clears `smoId` + `centerDeletedAt` on every Projects.employees[] entry
  //    referencing this center, so the SMO Lead Reports fallback no longer
  //    resurrects the assignment. Preserves all Visits / CalendarJobs data.
  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    const { centerId } = confirmRemove;
    setRemoving(true);
    try {
      await updateDoc(doc(db, 'Centers', centerId), { SMO: deleteField() });

      const projectsSnap = await getDocs(collection(db, 'Projects'));
      let updatedProjects = 0;
      let updatedEmployees = 0;
      for (const projectDoc of projectsSnap.docs) {
        const data = projectDoc.data();
        const employees = data.employees || [];
        let hasChanges = false;
        const next = employees.map((emp) => {
          if (emp.centerId === centerId && (emp.smoId || emp.centerDeletedAt)) {
            hasChanges = true;
            updatedEmployees++;
            const { smoId, centerDeletedAt, ...rest } = emp;
            return rest;
          }
          return emp;
        });
        if (hasChanges) {
          await updateDoc(doc(db, 'Projects', projectDoc.id), { employees: next });
          updatedProjects++;
        }
      }

      // Update local view immediately
      setWorkerCounts((prev) => prev); // no-op, kept for clarity
      toast.success(
        updatedEmployees > 0
          ? msg('smoRemovedCount', { projects: updatedProjects, count: updatedEmployees })
          : msg('smoRemoved')
      );
      setConfirmRemove(null);
      if (onCenterUpdated) onCenterUpdated();
    } catch (err) {
      console.error('Error removing SMO from center:', err);
      toast.error(msg('smoRemoveError'));
    } finally {
      setRemoving(false);
    }
  };

  const statTiles = [
    { key: 'smoCount', icon: LuUsersRound, value: stats.smoCount, label: label('smo.stats.smoCount'), tone: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300' },
    { key: 'withSmo', icon: LuBuilding2, value: stats.totalSmoCenters, label: label('smo.stats.withSmo'), tone: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300' },
    { key: 'active', icon: LuCircleCheck, value: stats.activeSmoCenters, label: label('smo.stats.active'), tone: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' },
    { key: 'orphan', icon: LuTriangleAlert, value: stats.orphanCount, label: label('smo.stats.orphan'), tone: 'tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-300' },
    { key: 'average', icon: LuChartColumn, value: stats.avg, label: label('smo.stats.average'), tone: 'tw-bg-slate-900/[0.06] tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300' },
  ];
  // The old list stayed blank when a search hid every SMO while centers without one existed
  const showEmpty = filteredSmoGroups.length === 0 && (centersWithoutSmo.length === 0 || Boolean(search));
  const location = (c) => [c['İl'], c['İlçe']].filter(Boolean).join(' / ') || label('smo.noLocation');

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title={label('smo.title')}
        subtitle={label('smo.subtitle')}
        closeLabel={t('common.close')}
        lang={language}
        bodyClassName="tw-px-4 tw-pb-5 sm:tw-px-6"
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
            <LuUsersRound aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={<Button onClick={onClose}>{label('common.close')}</Button>}
      >
        <div className="tw-grid tw-grid-cols-2 tw-gap-2.5 tw-pt-4 sm:tw-grid-cols-3 sm:tw-pt-5 lg:tw-grid-cols-5">
          {statTiles.map((tile, index) => {
            const Icon = tile.icon;
            return (
              <div key={tile.key} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03] ${index === statTiles.length - 1 ? 'max-sm:tw-col-span-2' : ''}`}>
                <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tile.tone}`}>
                  <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
                </span>
                <span className="tw-min-w-0">
                  <span className="tw-block tw-text-xl tw-font-bold tw-leading-tight tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{tile.value}</span>
                  <span className="tw-block tw-text-xs tw-font-medium tw-leading-tight tw-text-slate-500 dark:tw-text-slate-400">{tile.label}</span>
                </span>
              </div>
            );
          })}
        </div>

        {/* Search and list actions stay on top while the list scrolls */}
        <div className="tw-sticky tw-top-0 tw-z-10 -tw-mx-4 tw-mb-3 tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-bg-white/90 tw-px-4 tw-py-3 tw-backdrop-blur-md sm:-tw-mx-6 sm:tw-px-6 dark:tw-bg-[#1f232b]/90">
          <SearchField value={search} onChange={setSearch} placeholder={label('smo.search')} className="tw-min-w-[14rem] tw-flex-1" />
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
            <Button variant="ghost" size="sm" onClick={expandAll}>
              <LuChevronsUpDown aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('smo.expandAll')}
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>
              <LuChevronsDownUp aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('smo.collapseAll')}
            </Button>
            <Button size="sm" onClick={exportExcel} title={label('smo.excelHint')}>
              <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
              {label('smo.excel')}
            </Button>
          </div>
        </div>

        <div className="tw-space-y-3">
          {showEmpty && <EmptyState icon={LuUserSearch} title={label('smo.empty')} />}

          {filteredSmoGroups.map((g) => {
            const isOpen = !!expanded[g.id];
            const active = g.centers.filter((c) => c['Durum'] === 'Aktif').length;
            const inactive = g.centers.length - active;
            const totalWorkers = g.centers.reduce(
              (acc, c) => acc + (workerCounts[c.id] || 0),
              0
            );
            return (
              <section key={g.id} className={`tw-overflow-hidden tw-rounded-2xl tw-border tw-transition-colors ${isOpen ? 'tw-border-violet-300/70 tw-bg-white tw-shadow-[0_12px_30px_-18px_rgba(76,29,149,0.35)] dark:tw-border-violet-400/25 dark:tw-bg-white/[0.04]' : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.02]'}`}>
                <button
                  type="button"
                  onClick={() => toggleExpand(g.id)}
                  aria-expanded={isOpen}
                  className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-bg-transparent tw-p-3 tw-text-left tw-transition-colors hover:tw-bg-slate-900/[0.03] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-violet-500/40 sm:tw-p-4 dark:hover:tw-bg-white/[0.03]"
                >
                  <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="avatarLg">
                    {getInitials(g.name, g.surname)}
                  </GlassIcon>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{g.fullName}</span>
                    <span className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                      <Pill icon={LuBuilding2}>{label('smo.centerCount', { count: g.centers.length })}</Pill>
                      <Pill tone="success">{label('smo.activeCount', { count: active })}</Pill>
                      {inactive > 0 && <Pill tone="muted">{label('smo.inactiveCount', { count: inactive })}</Pill>}
                      <Pill icon={LuUsers}>{loadingWorkers ? '…' : label('smo.workerCount', { count: totalWorkers })}</Pill>
                    </span>
                  </span>
                  <LuChevronDown aria-hidden="true" className={`tw-h-5 tw-w-5 tw-shrink-0 tw-text-slate-400 tw-transition-transform tw-duration-200 ${isOpen ? 'tw-rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <ul className="tw-space-y-1.5 tw-border-t tw-border-slate-200/70 tw-bg-slate-50/60 tw-p-2 sm:tw-p-3 dark:tw-border-white/10 dark:tw-bg-black/10">
                    {g.centers.map((c) => {
                      const emps = centerEmployees[c.id] || [];
                      const isCenterOpen = !!expandedCenters[c.id];
                      const hasEmps = emps.length > 0;
                      return (
                        <li key={c.id} className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                          <div className="tw-flex tw-items-center tw-gap-1 tw-pr-2">
                            <button
                              type="button"
                              disabled={!hasEmps}
                              onClick={() => hasEmps && toggleCenter(c.id)}
                              aria-expanded={hasEmps ? isCenterOpen : undefined}
                              title={hasEmps ? label('smo.showEmployees') : undefined}
                              className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors enabled:hover:tw-bg-slate-900/[0.03] disabled:tw-cursor-default focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-violet-500/40 dark:enabled:hover:tw-bg-white/[0.03]"
                            >
                              <span className="tw-flex tw-min-w-0 tw-flex-1 tw-basis-56 tw-items-center tw-gap-2.5">
                                <span className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300">
                                  <LuBuilding2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                                </span>
                                <span className="tw-min-w-0">
                                  <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{c['Adı'] || '—'}</span>
                                  <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                                    <LuMapPin aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                                    <span className="tw-truncate">{location(c)}</span>
                                  </span>
                                </span>
                              </span>
                              <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                                <span title={label('smo.workersHint')}>
                                  <Pill icon={LuUsers}>{workerCounts[c.id] || 0}</Pill>
                                </span>
                                <StatusBadge value={c['Durum'] || '—'} />
                                {hasEmps && (
                                  <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-text-slate-400 tw-transition-transform tw-duration-200 ${isCenterOpen ? 'tw-rotate-180' : ''}`} />
                                )}
                              </span>
                            </button>
                            <IconButton
                              label={label('smo.removeHint')}
                              tone="alert"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmRemove({
                                  centerId: c.id,
                                  centerName: c['Adı'] || '—',
                                  smoName: g.fullName,
                                });
                              }}
                              className="tw-shrink-0"
                            >
                              <LuUserX aria-hidden="true" className="tw-h-4 tw-w-4" />
                            </IconButton>
                          </div>

                          {isCenterOpen && hasEmps && (
                            <div className="tw-border-t tw-border-slate-200/70 tw-px-3 tw-pb-3 tw-pt-2.5 dark:tw-border-white/10">
                              <p className="tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-400 dark:tw-text-slate-500">
                                <LuUsers aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                                {label('smo.employeesTitle', { count: emps.length })}
                              </p>
                              <ul className="tw-grid tw-gap-1.5 md:tw-grid-cols-2">
                                {emps.map((emp, idx) => (
                                  <li
                                    key={`${emp.id}-${emp.projectId}-${idx}`}
                                    className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-slate-900/[0.03] tw-px-2.5 tw-py-2 dark:tw-bg-white/[0.03]"
                                  >
                                    <span aria-hidden="true" className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-slate-400 tw-to-slate-600 tw-text-[11px] tw-font-bold tw-text-white">
                                      {getInitials(emp.name, emp.surname)}
                                    </span>
                                    <span className="tw-min-w-0 tw-flex-1">
                                      <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">
                                        {`${emp.name} ${emp.surname}`.trim() || '—'}
                                      </span>
                                      {(emp.role || emp.projectTitle) && (
                                        <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                                          {[emp.role, emp.projectTitle].filter(Boolean).join(' · ')}
                                        </span>
                                      )}
                                    </span>
                                    <span className="tw-flex tw-shrink-0 tw-flex-wrap tw-justify-end tw-gap-1">
                                      {emp.workerType && (
                                        <Pill tone={emp.workerType.toLowerCase() === 'primer' ? 'teal' : 'amber'}>{emp.workerType}</Pill>
                                      )}
                                      {emp.blindStatus && (
                                        <Pill tone={emp.blindStatus.toLowerCase() === 'blinded' ? 'violet' : 'sky'}>{emp.blindStatus}</Pill>
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}

          {centersWithoutSmo.length > 0 && !search && (
            <section className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-amber-300/60 tw-bg-amber-50/60 dark:tw-border-amber-400/20 dark:tw-bg-amber-400/[0.05]">
              <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 sm:tw-p-4">
                <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="avatarLg">
                  <LuTriangleAlert aria-hidden="true" className="tw-h-5 tw-w-5" />
                </GlassIcon>
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-block tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('smo.orphanTitle')}</span>
                  <span className="tw-mt-1.5 tw-flex">
                    <Pill tone="amber">{label('smo.orphanCount', { count: centersWithoutSmo.length })}</Pill>
                  </span>
                </span>
              </div>
              <ul className="tw-space-y-1.5 tw-border-t tw-border-amber-300/50 tw-p-2 sm:tw-p-3 dark:tw-border-amber-400/15">
                {centersWithoutSmo.map((c) => (
                  <li key={c.id} className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-basis-56 tw-items-center tw-gap-2.5">
                      <span className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300">
                        <LuBuilding2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </span>
                      <span className="tw-min-w-0">
                        <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{c['Adı'] || '—'}</span>
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                          <LuMapPin aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                          <span className="tw-truncate">{location(c)}</span>
                        </span>
                      </span>
                    </span>
                    <StatusBadge value={c['Durum'] || '—'} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(confirmRemove)}
        onClose={() => !removing && setConfirmRemove(null)}
        size="md"
        title={label('smo.remove.title')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30" size="xs">
            <LuUserX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmRemove(null)} disabled={removing}>{label('common.cancel')}</Button>
            <Button variant="danger" onClick={handleConfirmRemove} disabled={removing} className="max-sm:tw-flex-1">
              {removing
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
                : <LuUserX aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {removing ? label('smo.remove.removing') : label('smo.remove.confirm')}
            </Button>
          </>
        )}
      >
        {confirmRemove && (
          <div className="tw-space-y-4">
            <p className="tw-text-[15px] tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">
              <RichText segments={richText('smo.remove.lead', { center: confirmRemove.centerName, smo: confirmRemove.smoName })} />
            </p>
            <HelpNote>
              <strong className="tw-block tw-font-semibold">{label('smo.remove.whatTitle')}</strong>
              <ul className="tw-mt-1.5 tw-list-disc tw-space-y-1 tw-pl-4">
                <li>{label('smo.remove.itemSmo')}</li>
                <li>{label('smo.remove.itemKept')}</li>
                <li>{label('smo.remove.itemLater')}</li>
              </ul>
            </HelpNote>
          </div>
        )}
      </Modal>
    </>
  );
};

SMOOverviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  centers: PropTypes.arrayOf(PropTypes.object),
  employeesMap: PropTypes.object.isRequired,
  onCenterUpdated: PropTypes.func,
};

export default SMOOverviewModal;
